// Browser-side session lifecycle: the ONE place that (a) sends the keepalive
// heartbeat and reacts to its status, (b) kicks a tab to the login page when
// the server says the session is gone, and (c) logs out. Every logout button
// and every "401 → login" branch must go through here so the heartbeat
// keepalive and logout cannot race each other, and so the login target can
// never regress to '/login' (#775) or loop on '/' (#776).
//
// Decisions are in session-revocation-core.ts (pure); this file is the DOM
// wiring. Deps are injectable for tests.

import { reportError } from '@/lib/error-reporting';
import {
  isSessionGoneStatus,
  kickSuppressed,
  loginKickHref,
  KICK_DELETE_TIMEOUT_MS,
  LOGOUT_AWAIT_HEARTBEAT_MS,
  LOGOUT_DELETE_TIMEOUT_MS,
} from './session-revocation-core';
import { clientEpochHeaders, STALE_CLIENT_CODE, STALE_CLIENT_STATUS } from './client-epoch';

const KICK_STAMP_KEY = 'rehaut:last-session-kick';
const STALE_RELOAD_STAMP_KEY = 'rehaut:last-stale-reload';
/** A second stale-client reload inside this window means the reload did not
 *  bring the new bundle (CDN / cache) — stop instead of looping. */
export const STALE_RELOAD_WINDOW_MS = 120_000;

export type SessionClientDeps = {
  fetchImpl?: typeof fetch;
  navigate?: (href: string) => void;
  now?: () => number;
  /** null = no storage available (loop breaker degrades to "always kick") */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  location?: { pathname: string; search: string };
};

export type ReloadDeps = SessionClientDeps & {
  reload?: () => void;
};

export type HeartbeatDeps = ReloadDeps & {
  /** Called after a heartbeat the server ACCEPTED (any non-401 status). The
   *  heartbeat RESPONSE is what re-mints sb_jwt (RT-1), so this is the one
   *  moment the caller KNOWS the cookie may have just rotated. */
  onRenewed?: () => void;
};

// Module state — one tab, one session. (Per-TAB: a second tab of the same
// browser has its own copy — see the cross-tab note on logoutAndRedirect.)
let loggingOut = false;
const inflightHeartbeats = new Set<Promise<unknown>>();

/** True once a logout/kick has started; the heartbeat must not fire again. */
export function isLoggingOut(): boolean {
  return loggingOut;
}

/** Register a heartbeat request so logout can wait for it. Tracks EVERY
 *  in-flight one (a hung ping can overlap the next 60s tick). */
export function trackHeartbeat<T>(p: Promise<T>): Promise<T> {
  const tracked: Promise<unknown> = p.then(
    () => { inflightHeartbeats.delete(tracked); },
    () => { inflightHeartbeats.delete(tracked); },
  );
  inflightHeartbeats.add(tracked);
  return p;
}

function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

async function awaitInflightHeartbeats(): Promise<void> {
  if (inflightHeartbeats.size === 0) return;
  await Promise.race([Promise.all(Array.from(inflightHeartbeats)), sleep(LOGOUT_AWAIT_HEARTBEAT_MS)]);
}

/** DELETE /api/auth (clears rehaut-session + sb_jwt; needs no session), bounded
 *  so a hung request can never pin the tab. Rejections and timeouts both fall
 *  through to the caller's navigation. On timeout the request is ABORTED, not
 *  merely raced past: a DELETE still in flight when we navigate could land
 *  after a fast re-login on the same tab and wipe the fresh cookies. Abort
 *  tears it down before that can happen (and no `keepalive`, for the same
 *  reason). Fetch mocks that ignore the signal are still bounded by the race. */
async function boundedLogoutDelete(f: typeof fetch, timeoutMs: number): Promise<void> {
  const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => { ac?.abort(); resolve(); }, timeoutMs);
  });
  try {
    await Promise.race([
      f('/api/auth', { method: 'DELETE', credentials: 'include', signal: ac?.signal }),
      timeout,
    ]);
  } catch {
    // network error / abort — fall through to navigation
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    return null; // Safari private mode / disabled storage
  }
}

function readStamp(storage: Pick<Storage, 'getItem'> | null, key: string = KICK_STAMP_KEY): number | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStamp(storage: Pick<Storage, 'setItem'> | null, nowMs: number, key: string = KICK_STAMP_KEY): void {
  if (!storage) return;
  try { storage.setItem(key, String(nowMs)); } catch { /* ignore */ }
}

/** Is this response the client-epoch gate's 409 (src/lib/client-epoch.ts)? */
export async function isStaleClientResponse(res: Response): Promise<boolean> {
  if (res.status !== STALE_CLIENT_STATUS) return false;
  try {
    const body = (await res.clone().json()) as { code?: unknown };
    return body?.code === STALE_CLIENT_CODE;
  } catch {
    return false;
  }
}

/** The server says this tab runs a bundle from an older CLIENT_EPOCH (a deploy
 *  changed something the old bundle would get wrong — see client-epoch.ts).
 *  Reload the page: same session, new bundle, no logout. Loop guard: a second
 *  stale verdict within STALE_RELOAD_WINDOW_MS means the reload did not bring
 *  the new bundle — report it and stay put rather than reload forever.
 *  Returns whether a reload was triggered. */
export function reloadStaleClient(reason: string, deps: ReloadDeps = {}): boolean {
  if (typeof window === 'undefined') return false;
  if (loggingOut) return false; // a kick / logout is already navigating
  const nowMs = (deps.now ?? Date.now)();
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  const last = readStamp(storage, STALE_RELOAD_STAMP_KEY);
  if (last !== null && nowMs - last >= 0 && nowMs - last < STALE_RELOAD_WINDOW_MS) {
    reportError(new Error('stale-client reload suppressed (loop guard)'), {
      context: 'session:stale-reload-loop-guard',
      extra: { reason },
    });
    return false;
  }
  writeStamp(storage, nowMs, STALE_RELOAD_STAMP_KEY);
  (deps.reload ?? (() => window.location.reload()))();
  return true;
}

/** The 60s keepalive ping (DashboardLayout). fetch() does NOT reject on 401,
 *  so the status is read here: 401 = the server refused to renew — cookie
 *  gone/expired/invalid, or the staff row was DEACTIVATED (the clinic's only
 *  revocation lever) — and the tab is kicked to login. Anything else counts
 *  as "still authorized" (5xx = blip; the server fails open on DB errors) and
 *  hands the possibly-rotated sb_jwt to the realtime socket via onRenewed. */
export async function runHeartbeat(deps: HeartbeatDeps = {}): Promise<void> {
  if (typeof window === 'undefined') return;
  // Never send a heartbeat once logout/kick has begun: its RESPONSE
  // Set-Cookies a fresh session + sb_jwt and would silently log the shared
  // front-desk iPad back in after DELETE /api/auth cleared them.
  if (loggingOut) return;
  const f = deps.fetchImpl ?? fetch;
  let res: Response;
  try {
    // The epoch header lets the server move tabs off a stale bundle
    // (client-epoch.ts). A bundle WITHOUT it — anything older than the gate —
    // is answered 401 and kicked below, which is the only lever it obeys.
    res = await trackHeartbeat(f('/api/heartbeat', { method: 'POST', credentials: 'include', headers: clientEpochHeaders() }));
  } catch {
    return; // network error: nothing to conclude, next tick retries
  }
  if (isSessionGoneStatus(res.status)) {
    await kickToLogin('heartbeat-401', deps);
    return;
  }
  if (await isStaleClientResponse(res)) {
    reloadStaleClient('heartbeat-409', deps);
    return; // not renewed: the reloaded page heartbeats on mount
  }
  deps.onRenewed?.();
}

/** Explicit logout (button). Order matters:
 *   1. flag → no NEW heartbeat is sent from this tab;
 *   2. wait (bounded) for the heartbeats already in flight — their responses
 *      Set-Cookie a fresh session + sb_jwt and must land BEFORE we clear;
 *   3. DELETE /api/auth (bounded) clears rehaut-session + sb_jwt;
 *   4. hard-navigate to '/' (the login page).
 *  Navigation happens even if the DELETE failed or timed out: with cookies
 *  still valid the proxy bounces '/' → /home, which is the honest "logout did
 *  not stick" signal, and far better than a button that appears to do nothing.
 *
 *  KNOWN RESIDUAL (cross-tab): this guard is per tab. A SECOND tab of the same
 *  browser runs its own 60s heartbeat; if that tab's ping is in flight when
 *  this tab's DELETE lands, its response re-sets both cookies and the device
 *  stays logged in (probability ≈ RTT/60s per extra open tab). A plain logout
 *  deliberately does not revoke the staffer's OTHER devices, so it cannot close
 *  this; that is out of scope here — use logoutEverywhereAndRedirect, which revokes
 *  server-side (session version bump), when it matters. */
export async function logoutAndRedirect(deps: SessionClientDeps = {}): Promise<void> {
  if (typeof window === 'undefined') return;
  loggingOut = true;
  await awaitInflightHeartbeats();
  await boundedLogoutDelete(deps.fetchImpl ?? fetch, LOGOUT_DELETE_TIMEOUT_MS);
  (deps.navigate ?? ((h: string) => window.location.assign(h)))('/');
}

export type LogoutEverywhereDeps = SessionClientDeps & {
  /** Confirmation prompt; resolves true to proceed. Default window.confirm. */
  confirm?: (message: string) => boolean;
  /** Failure notice. Default window.alert. */
  notify?: (message: string) => void;
};

export const LOGOUT_ALL_CONFIRM =
  '¿Cerrar sesión en todos tus dispositivos? Tendrás que volver a iniciar sesión en cada uno.';
export const LOGOUT_ALL_FAILED =
  'No se pudo cerrar la sesión en los demás dispositivos. Intenta de nuevo.';

/** "Cerrar sesión en todos los dispositivos" (profile menu). POST
 *  /api/auth/logout-all bumps staff.session_version server-side, which kills
 *  every session cookie and sb_jwt of this staffer on every device — including
 *  copies, and including a SECOND TAB of this browser whose in-flight heartbeat
 *  re-sets the old cookie after we clear it (the cross-tab residual of
 *  logoutAndRedirect: that cookie carries the old version and is rejected on
 *  its next request). Same ordering as logoutAndRedirect (flag → wait for
 *  in-flight heartbeats → server call → DELETE /api/auth → '/'), but if the
 *  revocation itself FAILS the tab stays put and says so: pretending the other
 *  devices were logged out would be worse than a visible error. */
export async function logoutEverywhereAndRedirect(deps: LogoutEverywhereDeps = {}): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const confirmFn = deps.confirm ?? ((m: string) => window.confirm(m));
  if (!confirmFn(LOGOUT_ALL_CONFIRM)) return false;
  const f = deps.fetchImpl ?? fetch;
  loggingOut = true;
  await awaitInflightHeartbeats();
  let ok = false;
  try {
    const res = await f('/api/auth/logout-all', { method: 'POST', credentials: 'include', headers: clientEpochHeaders() });
    // 401 = this session was ALREADY revoked — the goal state; go to login.
    ok = res.ok || isSessionGoneStatus(res.status);
  } catch {
    ok = false;
  }
  if (!ok) {
    loggingOut = false; // the session is still live: resume the heartbeat
    (deps.notify ?? ((m: string) => window.alert(m)))(LOGOUT_ALL_FAILED);
    return false;
  }
  await boundedLogoutDelete(f, LOGOUT_DELETE_TIMEOUT_MS);
  (deps.navigate ?? ((h: string) => window.location.assign(h)))('/');
  return true;
}

/** Server said the session is gone (401 from the heartbeat or /api/data):
 *  the cookie is missing/expired/invalid, OR the staff row was deactivated
 *  (the heartbeat's revocation gate — the ONLY way the clinic ends a
 *  departed employee's open tab). The server already cleared both cookies on
 *  a route-level 401; a proxy-level 401 clears sb_jwt only. We clear again via
 *  DELETE /api/auth (belt and braces — DELETE needs no session; BOUNDED so a
 *  hung network cannot freeze the kick), then hard-navigate to the login page
 *  with ?next= so a merely-expired session lands back where it was. */
export async function kickToLogin(reason: string, deps: SessionClientDeps = {}): Promise<void> {
  if (typeof window === 'undefined') return;
  // A kick/logout is already under way in THIS page (e.g. /api/data and the
  // heartbeat both 401 in the same tick): one navigation is enough. Module
  // state resets on reload, so this never masks the reload-loop case below.
  if (loggingOut) return;
  const loc = deps.location ?? window.location;
  const href = loginKickHref(loc.pathname, loc.search);
  if (!href) return; // on '/', a 401 is the logged-out steady state (#776)
  const nowMs = (deps.now ?? Date.now)();
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  // sessionStorage survives the reload; a second kick within the window
  // means '/' bounced a still-valid cookie back in — refuse to loop.
  if (kickSuppressed(readStamp(storage), nowMs)) {
    reportError(new Error('session kick suppressed (loop guard)'), {
      context: 'session:kick-loop-guard',
      extra: { reason },
    });
    return;
  }
  writeStamp(storage, nowMs);
  loggingOut = true; // suppress further heartbeats from this tab
  await boundedLogoutDelete(deps.fetchImpl ?? fetch, KICK_DELETE_TIMEOUT_MS);
  (deps.navigate ?? ((h: string) => window.location.assign(h)))(href);
}

/** Test hook — reset module state between cases. */
export function _resetSessionClientForTests(): void {
  loggingOut = false;
  inflightHeartbeats.clear();
}
